/**
 * Message Store
 *
 * Hält die Konversationsliste + die Gesamt-Ungelesen-Zahl fürs Sidebar-Badge
 * (siehe AdminLayout.vue - pollt startUnreadPolling() alle ~45s, kein
 * WebSocket/SSE im Projekt vorhanden).
 */
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { messageService, type ConversationSummary, type Contact } from '@/modules/messages/services/message.service'

export type StoreStatus = 'idle' | 'loading' | 'success' | 'error'

export const useMessageStore = defineStore('messages', () => {
  const conversations = ref<ConversationSummary[]>([])
  const contacts = ref<Contact[]>([])
  const unreadTotal = ref(0)
  const status = ref<StoreStatus>('idle')
  const error = ref<string | null>(null)

  const hasUnread = computed(() => unreadTotal.value > 0)

  /** Für die Popup-Benachrichtigung (MessageToast.vue) - wird gesetzt, sobald
   * ein Polling-Tick eine gestiegene Ungelesen-Zahl feststellt (nicht schon
   * beim allerersten Poll nach dem Laden der Seite, siehe hasPolledOnce). */
  const latestNotification = ref<{ conversationId: string; title: string; preview: string } | null>(null)
  let hasPolledOnce = false

  function clearNotification(): void {
    latestNotification.value = null
  }

  async function fetchConversations(): Promise<void> {
    status.value = 'loading'
    error.value = null
    try {
      const res = await messageService.conversations()
      conversations.value = res.items
      status.value = 'success'
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Fehler beim Laden der Nachrichten'
      status.value = 'error'
      console.error('fetchConversations error:', err)
    }
  }

  async function fetchContacts(): Promise<void> {
    try {
      const res = await messageService.contacts()
      contacts.value = res.items
    } catch (err) {
      console.error('fetchContacts error:', err)
    }
  }

  async function refreshUnreadCount(): Promise<void> {
    try {
      const newCount = await messageService.unreadCount()
      if (hasPolledOnce && newCount > unreadTotal.value) {
        // Neue Nachricht(en) seit dem letzten Poll - Konversationsliste neu
        // laden, um Absender/Vorschau der neuesten Nachricht zu bekommen
        // (Liste ist bereits nach lastMessageAt absteigend sortiert).
        await fetchConversations()
        const mostRecent = conversations.value[0]
        if (mostRecent) {
          latestNotification.value = {
            conversationId: mostRecent.id,
            title: mostRecent.title,
            preview: mostRecent.lastMessage?.body ?? ''
          }
        }
      }
      hasPolledOnce = true
      unreadTotal.value = newCount
    } catch (err) {
      // Badge-Polling darf bei einem einzelnen Fehlschlag nicht laut werden -
      // nächster Poll-Tick versucht es einfach erneut.
      console.error('refreshUnreadCount error:', err)
    }
  }

  let pollHandle: ReturnType<typeof setInterval> | null = null
  function startUnreadPolling(intervalMs = 45000): void {
    if (pollHandle) return
    void refreshUnreadCount()
    pollHandle = setInterval(() => void refreshUnreadCount(), intervalMs)
  }
  function stopUnreadPolling(): void {
    if (pollHandle) {
      clearInterval(pollHandle)
      pollHandle = null
    }
  }

  return {
    conversations,
    contacts,
    unreadTotal,
    hasUnread,
    latestNotification,
    status,
    error,
    fetchConversations,
    fetchContacts,
    refreshUnreadCount,
    startUnreadPolling,
    stopUnreadPolling,
    clearNotification
  }
})
